import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import type { GridColDef } from "@mui/x-data-grid";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import { LojaDataGrid } from "layouts/pages/loja/common";
import { setMessage, useMaterialUIController } from "context";

import ModalSelectPayment from "./components/ModalSlectPayment";
import {
  TIPO_PAY_ACCENT,
  tipoPayToolbarSx,
} from "./tipos-pagamento-evento.theme";

import { IEventoResponse } from "interfaces/eventos";
import { ITipoPagamento } from "interfaces/payment";
import { deleteTypePayEvento } from "services/evento";
import { getAllPaymentActive } from "services/payments";

interface Props {
  evento: IEventoResponse;
  updateEvento: () => void;
}

type PaymentEventoRow = IEventoResponse["formaPaymentoEvento"][number];

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={accent ? TIPO_PAY_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function TiposPagamentosEventos({
  evento,
  updateEvento,
}: Props) {
  const [, dispatch] = useMaterialUIController();
  const [formOpen, setFormOpen] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [selectedPayment, setSelectedPayment] =
    useState<PaymentEventoRow | null>(null);
  const [allTypePayment, setAllTypePayment] = useState<ITipoPagamento[]>([]);
  const [loadingTipos, setLoadingTipos] = useState(true);

  const payments = evento.formaPaymentoEvento ?? [];

  const loadTiposAtivos = useCallback(async () => {
    setLoadingTipos(true);
    try {
      const resp = await getAllPaymentActive();
      setAllTypePayment(
        resp.data.filter((payment) => payment.status === true),
      );
    } finally {
      setLoadingTipos(false);
    }
  }, []);

  useEffect(() => {
    void loadTiposAtivos();
  }, [loadTiposAtivos]);

  const disponiveis = useMemo(
    () =>
      allTypePayment.filter(
        (item) =>
          !payments.some(
            (linked) =>
              linked.TiposPagamento.tipoPagamento === item.tipoPagamento,
          ),
      ),
    [allTypePayment, payments],
  );

  const stats = useMemo(() => {
    const percentual = payments.filter(
      (item) => item.TiposPagamento.taxaIsPercentage,
    ).length;
    return {
      vinculados: payments.length,
      disponiveis: disponiveis.length,
      percentual,
    };
  }, [payments, disponiveis]);

  const handleDeletePayment = async () => {
    if (!selectedPayment?.id) return;
    try {
      await deleteTypePayEvento(selectedPayment.id);
      setSelectedPayment(null);
      setOpenDialogDelete(false);
      updateEvento();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        text: error instanceof Error ? error.message : "Falha ao excluir.",
        severity: "error",
      });
    }
  };

  const columns: GridColDef<PaymentEventoRow>[] = useMemo(
    () => [
      {
        field: "tipoPagamento",
        headerName: "Tipo de pagamento",
        flex: 1,
        minWidth: 180,
        valueGetter: (params) => params.row.TiposPagamento.tipoPagamento,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={700} noWrap>
            {params.row.TiposPagamento.tipoPagamento}
          </Typography>
        ),
      },
      {
        field: "icon",
        headerName: "Ícone",
        width: 90,
        sortable: false,
        renderCell: (params) => (
          <Box
            component="img"
            src={params.row.TiposPagamento.icon}
            alt={params.row.TiposPagamento.tipoPagamento}
            sx={{ width: 56, height: 36, objectFit: "contain" }}
          />
        ),
      },
      {
        field: "taxa",
        headerName: "Taxa",
        width: 120,
        valueGetter: (params) => params.row.TiposPagamento.taxa_transacao,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600}>
            {params.row.TiposPagamento.taxaIsPercentage
              ? `${params.row.TiposPagamento.taxa_transacao}%`
              : `R$ ${params.row.TiposPagamento.taxa_transacao}`}
          </Typography>
        ),
      },
      {
        field: "acoes",
        headerName: "",
        width: 52,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="Remover do evento">
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setSelectedPayment(params.row);
                setOpenDialogDelete(true);
              }}
            >
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  return (
    <MDBox id="type-payment">
      <MDDialog
        title="Remover tipo de pagamento"
        message={`Deseja excluir o pagamento "${selectedPayment?.TiposPagamento.tipoPagamento}" deste evento?`}
        open={openDialogDelete}
        onClose={() => {
          setOpenDialogDelete(false);
          setSelectedPayment(null);
        }}
        response={(resp: boolean) => {
          if (resp) void handleDeletePayment();
          else setOpenDialogDelete(false);
        }}
      />

      <Box sx={tipoPayToolbarSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CreditCardOutlinedIcon sx={{ color: TIPO_PAY_ACCENT }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Tipos de pagamento
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {evento.titulo}
              </Typography>
            </Box>
          </Stack>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={() => setFormOpen(true)}
            startIcon={<AddOutlinedIcon />}
            disabled={disponiveis.length === 0}
            sx={{
              flexShrink: 0,
              background: `linear-gradient(195deg, ${TIPO_PAY_ACCENT}, #e04a1a)`,
            }}
          >
            Adicionar pagamento
          </MDButton>
        </Stack>
      </Box>

      {loadingTipos && payments.length === 0 ? (
        <Grid container spacing={2} mb={2.5}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={6} sm={4} key={i}>
              <Skeleton variant="rounded" height={88} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} mb={2.5}>
          <Grid item xs={6} sm={4}>
            <StatCard label="Vinculados" value={stats.vinculados} accent />
          </Grid>
          <Grid item xs={6} sm={4}>
            <StatCard
              label="Disponíveis para adicionar"
              value={stats.disponiveis}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Com taxa percentual"
              value={stats.percentual}
            />
          </Grid>
        </Grid>
      )}

      {payments.length === 0 ? (
        <Box
          sx={{
            py: 6,
            px: 2,
            textAlign: "center",
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "grey.300",
            bgcolor: "grey.50",
          }}
        >
          <CreditCardOutlinedIcon
            sx={{ fontSize: 44, color: "grey.400", mb: 1 }}
          />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Nenhum tipo de pagamento vinculado
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Informe quais formas de pagamento estarão disponíveis nas
            inscrições deste evento.
          </Typography>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => setFormOpen(true)}
            startIcon={<AddOutlinedIcon />}
            disabled={disponiveis.length === 0}
          >
            Adicionar primeiro pagamento
          </MDButton>
        </Box>
      ) : (
        <LojaDataGrid
          rows={payments}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
          disableColumnMenu
          disableRowSelectionOnClick
          rowHeight={64}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[5, 10, 25]}
          localeText={{ noRowsLabel: "Nenhum tipo de pagamento vinculado" }}
          sx={{
            "& .MuiDataGrid-row:hover": {
              bgcolor: "rgba(249, 96, 48, 0.04)",
            },
          }}
        />
      )}

      <ModalSelectPayment
        open={formOpen}
        handleClose={() => setFormOpen(false)}
        updateEvento={updateEvento}
        evento={evento}
        disponiveis={disponiveis}
      />
    </MDBox>
  );
}
